import React, { useReducer } from 'react';
import AuthContext from './authContext';
import AuthReducer from './authReducer';
import api from '../../api/api';
import {
    REGISTER_SUCCESS,
    USER_LOADED,
    LOGIN_SUCCESS,
    LOGOUT,
    FORGOT_PASSWORD,
    RESET_PASSWORD,
    UPDATE_PROFILE,
    UPDATE_PASSWORD,
    SET_LOADING,
    CLEAR_STATE,
    UPDATE_STORE_GROUP,
    GET_STATIC_LOOKUP_DATA,
    UPDATE_USER_STORES,
    VERIFY_PHONE,
} from '../types';
import { HEADERS } from '../../constants/headers';
import { MEXICO_COUNTRY_CODE } from 'src/constants/countryCodes';
import axios from 'axios';
import { handleError } from '../utils/handleError';
import { defaultUserTheme } from 'src/constants/colors/theme';
import { buildUserTheme } from '../utils/buildUserTheme';
import posthog from 'posthog-js';
import i18next from 'src/utils/i18next';
import { USER_LANGUAGE_STORAGE_KEY } from 'src/modules/user/constants';

const AuthState = (props) => {
    const initialState = {
        token: localStorage.getItem('token'),
        isAuthenticated: false,
        user: {},
        loading: false,
        staticLookupData: null,
        verifyPhone: null,
        verifyToken: null,
        verifyTokenError: null,
        verifyPhoneError: null,
        error: null,
    };

    const [state, dispatch] = useReducer(AuthReducer, initialState);

    const clearState = () => dispatch({ type: CLEAR_STATE });

    const register = async (values) => {
        try {
            const res = await api.post('/auth/register', values, HEADERS());
            dispatch({ type: REGISTER_SUCCESS, payload: res.data });

            loadUser();
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const updateStoreAuth = async (data, type) => {
        if (type === 'group') {
            dispatch({ type: UPDATE_STORE_GROUP, payload: data });
        }
        if (type === 'stores') {
            dispatch({ type: UPDATE_USER_STORES, payload: data });
        }
    };

    const getTheme = () => {
        const theme = state?.user?.theme || defaultUserTheme;
        return buildUserTheme(theme, state?.user?.theme?.mode || 'light');
    };

    //Set Current User
    const loadUser = async () => {
        try {
            const res = await api.get(`/auth/me`, HEADERS());

            const user = res.data.data;

            if (user.preferredLanguage) {
                localStorage.setItem(
                    USER_LANGUAGE_STORAGE_KEY,
                    user.preferredLanguage
                );
                await i18next.changeLanguage(user.preferredLanguage);
            }

            posthog.identify(user.email);

            posthog.capture('$set', {
                $set_once: {
                    email: user.email,
                    name: user.name,
                    _id: user._id,
                },
            });

            dispatch({
                type: USER_LOADED,
                payload: user,
            });
        } catch (error) {
            logout();
            dispatch(handleError(error));
        }
    };

    //Login User
    const login = async (values) => {
        setLoading();
        try {
            const res = await api.post('/auth/login', values, HEADERS());
            dispatch({
                type: LOGIN_SUCCESS,
                payload: res.data,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const handleVerifyPhone = async (phone) => {
        setLoading();
        try {
            const res = await api.post(
                '/twilio/verifyPhone',
                { phone, to: MEXICO_COUNTRY_CODE + phone },
                HEADERS()
            );
            dispatch({
                type: VERIFY_PHONE,
                payload: { ...res.data, phone },
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };
    const handleVerifyToken = async (code) => {
        setLoading();
        try {
            let { to, phone } = state.verifyPhone;
            const res = await api.post(
                '/twilio/verifyToken',
                { to, code, phone },
                HEADERS()
            );
            dispatch({
                type: LOGIN_SUCCESS,
                payload: res.data,
            });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Update profile
    const updateProfile = async (values, type) => {
        setLoading();

        let res;
        try {
            if (type === 'photo') {
                const uploadConfig = await api.post(
                    '/uploads/image',
                    { type: values.type, fileName: values.name },
                    HEADERS()
                );

                await axios.put(uploadConfig.data.url, values, {
                    headers: { 'Content-Type': values ? values.type : null },
                });

                const dataKey = uploadConfig.data.key;

                res = await api.put(
                    `/auth/updatedetails`,
                    { image: dataKey },
                    HEADERS()
                );
            } else {
                res = await api.put('/auth/updatedetails', values, HEADERS());
            }

            dispatch({
                type: UPDATE_PROFILE,
                payload: res.data,
            });

            loadUser();
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    // Logout
    const logout = () => dispatch({ type: LOGOUT });

    //Forgot Password
    const forgotPassword = async (email) => {
        try {
            const res = await api.post(
                '/auth/forgotpassword',
                { ...email },
                HEADERS()
            );
            dispatch({ type: FORGOT_PASSWORD, payload: res.data });
        } catch (error) {
            dispatch(handleError(error));
            throw error;
        }
    };

    //updatePassword
    const updatePassword = async (values) => {
        setLoading();

        try {
            const res = await api.put(
                '/auth/updatepassword',
                { ...values },
                HEADERS()
            );
            dispatch({ type: UPDATE_PASSWORD, payload: res.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    //Reset Password
    const resetPassword = async (values) => {
        try {
            const res = await api.put(
                `/auth/resetPassword/${values.token}/`,
                { password: values.password },
                HEADERS()
            );
            dispatch({ type: RESET_PASSWORD, payload: res.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const getStaticLookupData = async () => {
        setLoading();
        try {
            const res = await api.get(`/auth/staticLookupData`, HEADERS());
            dispatch({ type: GET_STATIC_LOOKUP_DATA, payload: res.data.data });
        } catch (error) {
            dispatch(handleError(error));
        }
    };

    const setLoading = () => dispatch({ type: SET_LOADING });

    return (
        <AuthContext.Provider
            value={{
                ...state,
                getStaticLookupData,
                register,
                login,
                loadUser,
                logout,
                forgotPassword,
                resetPassword,
                updateProfile,
                updatePassword,
                clearState,
                updateStoreAuth,
                handleVerifyPhone,
                handleVerifyToken,
                getTheme,
            }}
        >
            {props.children}
        </AuthContext.Provider>
    );
};

export default AuthState;
